/**
 * What a change to the stores register IS, said so the platform can do it.
 *
 * Every operation in storesEdit.ts does two things at once: it works out the
 * next register, which the screen shows straight away, and it says what it
 * did as a list of these steps. On the sample and the demonstration the next
 * register is simply kept, as it always was. Connected to a platform that
 * holds the register (routers/stores.py), the steps are sent instead, all or
 * none, and the platform — which may have seen stock move since this screen
 * last read it — has the last word.
 *
 * The steps are made by the same functions that make the change, rather than
 * worked out afterwards by comparing two registers, because a difference
 * cannot tell an issue against a reservation from an issue beside it, or a
 * count from an adjustment. Only the operation knows what it was.
 *
 * `applyOp` goes the other way: it replays a step on a register with the
 * same functions. The sample source uses it, and the tests use it to prove
 * that the steps an operation reports rebuild the register it produced.
 */
import { reorderRun, type StockItem, type StoresRegister } from "./stores";
import {
  addCategory,
  addItem,
  adjust,
  cancelOrder,
  count,
  decideRequisition,
  editItem,
  issue,
  orderFromRequisition,
  raiseReplenishment,
  raiseRequisition,
  receiveDelivery,
  receiveDirect,
  recordExternalOrder,
  releaseReservation,
  removeCategory,
  reserve,
  returnToStore,
  transfer,
  type Actor,
  type Result,
} from "./storesEdit";

/** The details of a line that can be set or corrected — everything but its number, bins and history. */
export interface ItemFields {
  name?: string;
  category?: string;
  uom?: string;
  unitCost?: number;
  manufacturer?: string;
  partNumber?: string;
  barcode?: string;
  min?: number;
  max?: number;
  leadDays?: number;
  supplier?: string;
  contractRef?: string;
  expiresAt?: string;
  note?: string;
  hazardous?: boolean;
  serves?: StockItem["serves"];
  /** a critical spare's reason; null takes the flag off */
  criticalWhy?: string | null;
}

export type StoresOp =
  | { op: "issue" | "return"; sku: string; qty: number; location: string; workOrder?: string; note?: string; at?: string }
  | { op: "adjust"; sku: string; qty: number; location: string; reason: string; reference?: string }
  | { op: "count"; sku: string; location: string; counted: number; note?: string; reference?: string }
  | { op: "transfer"; sku: string; qty: number; fromLocation: string; toLocation: string; note?: string }
  | { op: "reserve"; sku: string; qty: number; workOrder: string; neededAt?: string }
  | { op: "release"; id: string; reason: string }
  | {
      op: "item.create";
      item: ItemFields & { sku: string; name: string };
      bins: { location: string; bin?: string; qty: number }[];
      /** what the opening quantities are: a balance somebody stated, or a count somebody made */
      basis?: "adjust" | "count";
      reference?: string;
      openingNote?: string;
    }
  /** quiet: the correction is recorded by the load that made it, not by a movement of its own */
  | { op: "item.edit"; sku: string; changes: ItemFields; quiet?: boolean }
  | { op: "category.add"; label: string; what: string }
  | { op: "category.remove"; key: string }
  | {
      op: "requisition.raise";
      lines: { sku: string; qty: number; why: string; workOrder?: string; estimatedUnitCost?: number }[];
      department?: string;
      note?: string;
    }
  /** the reorder list, as the platform computes it, narrowed to the lines the screen showed */
  | { op: "replenish"; skus: string[] }
  | { op: "requisition.decide"; number: string; decision: "approved" | "rejected"; role: string; limit: number | null; note?: string }
  | { op: "requisition.order"; number: string; supplier: string; expectedAt?: string; contractRef?: string }
  | {
      op: "order.external";
      number: string;
      supplier: string;
      system: string;
      lines: { sku: string; qty: number; unitCost?: number }[];
      expectedAt?: string;
      reference?: string;
    }
  | {
      op: "order.receive";
      number: string;
      deliveryNote?: string;
      lines: { sku: string; qty: number; location: string; bin?: string }[];
      rejected?: { sku: string; qty: number; why: string }[];
    }
  | { op: "order.cancel"; number: string; reason: string }
  | {
      op: "receive.direct";
      lines: { sku: string; qty: number; location: string; bin?: string; unitCost?: number }[];
      supplier?: string;
      deliveryNote?: string;
      reason: string;
    };

/** The steps a result reports; none for a refusal. */
export function opsOf(result: { ok: boolean; ops?: StoresOp[] }): StoresOp[] {
  return result.ok ? (result.ops ?? []) : [];
}

/** Back from the step's shape to the line's. */
function fromFields(f: ItemFields): Partial<StockItem> {
  const { criticalWhy, ...rest } = f;
  const out: Partial<StockItem> = { ...rest };
  if (criticalWhy) out.critical = { why: criticalWhy };
  return out;
}

/**
 * Replay one step on a register, with the same functions the screen uses.
 *
 * An opening quantity on a new line is recorded as a movement here, as the
 * platform records it, so the ledger explains every unit on the shelf.
 */
export function applyOp(reg: StoresRegister, op: StoresOp, who: Actor, now = new Date()): Result {
  switch (op.op) {
    case "issue":
      return issue(reg, { sku: op.sku, qty: op.qty, location: op.location, workOrder: op.workOrder, note: op.note, at: op.at }, who, now);
    case "return":
      return returnToStore(reg, { sku: op.sku, qty: op.qty, location: op.location, workOrder: op.workOrder, note: op.note, at: op.at }, who, now);
    case "adjust":
      return adjust(reg, { sku: op.sku, qty: op.qty, location: op.location, reason: op.reason }, who, now);
    case "count":
      return count(reg, { sku: op.sku, location: op.location, counted: op.counted, note: op.note }, who, now);
    case "transfer":
      return transfer(reg, { sku: op.sku, qty: op.qty, from: op.fromLocation, to: op.toLocation, note: op.note }, who, now);
    case "reserve":
      return reserve(reg, { sku: op.sku, qty: op.qty, workOrder: op.workOrder, neededAt: op.neededAt }, who, now);
    case "release":
      return releaseReservation(reg, op.id, op.reason, who, now);
    case "item.create": {
      const fields = fromFields(op.item);
      const made = addItem(
        reg,
        {
          ...fields,
          sku: op.item.sku,
          name: op.item.name,
          category: fields.category ?? "consumable",
          uom: fields.uom ?? "each",
          min: fields.min ?? 0,
          max: fields.max ?? 0,
          bins: op.bins.map((b) => ({ location: b.location, bin: b.bin, onHand: b.qty })),
        },
        who,
      );
      if (!made.ok) return made;
      const next = made.register;
      const basis = op.basis ?? "adjust";
      const at = now.toISOString();
      const opening = op.bins
        .filter((b) => b.qty > 0)
        .map((b, i) => ({
          id: `mv-open-${at}-${op.item.sku}-${i}`,
          at,
          sku: op.item.sku,
          kind: basis,
          qty: b.qty,
          location: b.location,
          bin: b.bin,
          by: who.name,
          reference: op.reference,
          note: op.openingNote ?? (basis === "count" ? "Counted on to the register." : "Opening balance."),
        }));
      next.movements = [...next.movements, ...opening];
      if (basis === "count") {
        next.items = next.items.map((i) =>
          i.sku === op.item.sku ? { ...i, counted: { at: at.slice(0, 10), by: who.name, variance: 0 } } : i,
        );
      }
      return { ok: true, register: next, ops: [op] };
    }
    case "item.edit": {
      const { criticalWhy, ...rest } = op.changes;
      return editItem(
        reg,
        op.sku,
        { ...rest, ...(criticalWhy === undefined ? {} : { critical: criticalWhy ? { why: criticalWhy } : null }) },
        who,
        now,
      );
    }
    case "category.add":
      return addCategory(reg, { label: op.label, what: op.what }, who);
    case "category.remove":
      return removeCategory(reg, op.key, who);
    case "requisition.raise":
      return raiseRequisition(reg, { lines: op.lines, department: op.department, note: op.note }, who, now);
    case "replenish": {
      const wanted = new Set(op.skus);
      const run = reorderRun(reg, now)
        .map((g) => ({ ...g, lines: g.lines.filter((l) => wanted.has(l.item.sku)) }))
        .filter((g) => g.lines.length > 0);
      return raiseReplenishment(reg, run, who, now);
    }
    case "requisition.decide":
      return decideRequisition(
        reg,
        { number: op.number, decision: op.decision, authority: { role: op.role, limit: op.limit }, note: op.note },
        who,
        now,
      );
    case "requisition.order":
      return orderFromRequisition(
        reg,
        { number: op.number, supplier: op.supplier, expectedAt: op.expectedAt, contractRef: op.contractRef },
        who,
        now,
      );
    case "order.external":
      return recordExternalOrder(reg, op, who, now);
    case "order.receive":
      return receiveDelivery(
        reg,
        { orderNumber: op.number, deliveryNote: op.deliveryNote, lines: op.lines, rejected: op.rejected },
        who,
        now,
      );
    case "order.cancel":
      return cancelOrder(reg, op.number, op.reason, who);
    case "receive.direct":
      return receiveDirect(reg, op, who, now);
  }
}

/* ── what the platform answers ────────────────────────────────────────── */

/** The register as the platform holds it, and whether it holds one yet. */
export interface PlatformStores {
  register: StoresRegister;
  /** anything at all is on the platform: a line, a movement, a request, an order */
  held: boolean;
  /** a register is still kept as a note for this site and has not been brought across */
  noteWaiting: boolean;
  /** when the note was brought across, where it was */
  importedAt?: string;
}

/** The steps taken, one result each; `replayed` when the same request was sent twice and answered once. */
export interface StoresApplied {
  results: Record<string, unknown>[];
  replayed: boolean;
}

/** What the one-time move of the note brought across. */
export interface StoresImported {
  items: number;
  movements: number;
  /** adjustments written where the note's shelves and its movements disagreed */
  balancing: number;
  reservations: number;
  requisitions: number;
  orders: number;
  replayed: boolean;
}

/** Every step in turn, all or none — the first refusal is the answer, as it is on the platform. */
export function applyOps(reg: StoresRegister, ops: StoresOp[], who: Actor, now = new Date()): Result {
  let register = reg;
  for (const [i, op] of ops.entries()) {
    const r = applyOp(register, op, who, now);
    if (!r.ok) return ops.length > 1 ? { ok: false, why: `${i + 1}/${ops.length}: ${r.why}` } : r;
    register = r.register;
  }
  return { ok: true, register, ops };
}
